import { useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Flex } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import HeaderNavigation from 'components/HeaderNavigation';
import { FORM_MAX_WIDTH } from 'utils/constants';
import FooterButtons, { FOOTER_HEIGHT } from 'components/FooterButtons';
import FormBody from 'pages/Events/Form/components/FormBody';
import { BODY_SCHEMA, DEFAULT_VALUES, preparePayload } from 'pages/Events/Form/constants';
import ROUTES from 'app/routes';
import { API, APIRoutes } from 'api';
import { setFormErrors } from 'utils/api';
import LoadingIndicator from 'components/LoadingIndicator';
import parseDefaultValues from 'pages/Events/Form/Edit/parseDefaultValues';
import ComponentPreview from 'components/PreviewWrapper';
import { useGetEvent } from 'api/events';
import FormWrapper from 'components/FormWrapper';

function EventEdit() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const methods = useForm<EventItem>({
    mode: 'onChange',
    resolver: yupResolver(BODY_SCHEMA),
    defaultValues: DEFAULT_VALUES,
  });

  const { handleSubmit, setError, reset } = methods;

  const { data: { data: event } = {}, isFetching } = useGetEvent(+id);

  const { mutate: editEvent, isLoading: isEditing } = useMutation({
    mutationFn: (data: EventItem) => {
      const payload = preparePayload(data);
      return API.put(APIRoutes.events.byId(+id), payload);
    },
    mutationKey: 'eventsMutation',
    onSuccess: () => {
      toast.success('Zmiany w imprezie zostały zastosowane');
      navigate(ROUTES.events.base);
      void queryClient.invalidateQueries('events');
    },
    onError: ({ errors }) => {
      setFormErrors(setError, errors);
      toast.error('Wystąpił problem podczas edycji imprezy');
    },
  });

  const onSubmit = handleSubmit((data: EventItem) => editEvent(data));

  useEffect(() => {
    if (event) reset({ ...DEFAULT_VALUES, ...parseDefaultValues(event) });
  }, [event, reset]);

  const isLoading = isFetching || isEditing;

  return (
    <Box>
      <HeaderNavigation
        baseCrumb={{
          label: 'Imprezy',
          to: ROUTES.events.base,
        }}
        crumbs={[{ to: '', label: 'Edytuj imprezę' }]}
      />
      {isFetching && <LoadingIndicator />}
      <FormWrapper {...methods}>
        <Flex gap={4} mb={4} alignItems="flex-start" pb={FOOTER_HEIGHT}>
          <Box w="100%" as="form" id="event-editor" onSubmit={onSubmit} maxW={FORM_MAX_WIDTH}>
            <FormBody isLoading={isLoading} />
          </Box>
          <ComponentPreview kind="event" isEditMode />
        </Flex>
      </FormWrapper>
      <FooterButtons isLoading={isLoading} formId="event-editor" onCancel={() => navigate(-2)} />
    </Box>
  );
}

export default EventEdit;
